import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  styled,
} from "@mui/material";
import { CSSProperties, useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { Controller, useForm } from "react-hook-form";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { DropDownCentroCusto } from "../../../../../../common/components/dropdown_centro_custo";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { EquipamentoMedicaoForm, EquipamentoMedicaoSchema } from "../../../../../obras/presentation/schemas/Obras_equipamento_medicao/equipamentoMedicao";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Props = {
  open: boolean;
  saving: boolean;
  editmod: boolean;
  selectedEquipamentoMedicao?: EquipamentoMedicaoForm | null;
  onClose: () => void;
  onSubmit: (form: EquipamentoMedicaoForm) => void;
  onDownloadFile: (idEquipamentoMedicao: number, nroArquivo: number, nomeArquivo: string) => void;
  onOpenHistory: (idEquipamentoMedicao: number) => void;
};

export const ModalCreateEquipamentoMedicao = ({
  open,
  editmod,
  saving,
  selectedEquipamentoMedicao,
  onClose,
  onSubmit,
  onDownloadFile,
  onOpenHistory,
}: Props) => {

  const hookForm = useForm<EquipamentoMedicaoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(EquipamentoMedicaoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedEquipamentoMedicao) {
        hookForm.reset({
          ...selectedEquipamentoMedicao,
          arquivo: undefined,
          arquivo1: undefined,
          limparArquivos: false,
          limparArquivos1: false,
        });
      } else {
        hookForm.reset({
          // valores padrão para criação
          tipoRespEquipamento: 0,
          tipoCaliVeriiPor: 1,
          tipoFrequenciaData: 0,
          limparArquivos: false,
          responsavelCadastradoResp: false,
          tipoVerificacao: false,
        });
      }
    }
  }, [open, selectedEquipamentoMedicao, hookForm]);

  const nomeArquivo = hookForm.watch("nomeArquivo");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    hookForm.setValue("nomeArquivo", file.name);
    field.onChange(file);
  };

  const handleRemoveFile = (field: any) => {
    field.onChange(undefined);
    hookForm.setValue("limparArquivos", true);
    hookForm.setValue("nomeArquivo", null);
  };

  const nomeArquivo1 = hookForm.watch("nomeArquivo1");

  const handleFileChange1 = async (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    hookForm.setValue("nomeArquivo1", file.name);
    field.onChange(file);
  };

  const handleRemoveFile1 = (field: any) => {
    field.onChange(undefined);
    hookForm.setValue("limparArquivos1", true);
    hookForm.setValue("nomeArquivo1", null);
  };

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleSubmit = useCallback(
    (form: any) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit],
  );

  const tipoFrequencia = hookForm.watch("tipoFrequenciaData");
  const frequencia = hookForm.watch("frequenciaVerificao"); // em dias
  const dataVerificacao = hookForm.watch("dataVerificacao");
  const cor = hookForm.watch("cor") as CSSProperties['color'];

  useEffect(() => {
    if (tipoFrequencia === 1 && frequencia && dataVerificacao) {
      const proxData = dayjs(dataVerificacao).add(Number(frequencia), "day").toDate();
      hookForm.setValue("dataProxVerificacao", proxData);
    }
  }, [tipoFrequencia, frequencia, dataVerificacao, hookForm]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": { width: "100%", maxWidth: "90rem" },
        },
      }}
    >
      <Controller
        name="idEquipamentoMedicao"
        control={hookForm.control}
        render={({ field }) => <input type="hidden" {...field} value={field.value ?? ""} />}
      />

      <DialogTitle sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }} component="div">
        <h2>Cadastrar Equipamento de Medição</h2>
        <div
          onClick={e => handleClose()}>
          <DynamicIcon
            nameIcon="Close"
            size={25}
            className="text-black dark:text-white cursor-pointer"
          />
        </div>
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <ModalContainer>
            <Box display="flex" gap={4}>
              <Box flex={1} display="flex" flexDirection="column" gap={2}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <FormLabel>Equipamento</FormLabel>
                  <DefaultFormInput name="equipamento" label="Equipamento" reactHookForm={hookForm} required={true} />
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                  <FormLabel>Responsável pelo Equipamento</FormLabel>
                  {hookForm.watch("tipoRespEquipamento") === 1 ? (
                    <DefaultFormInput
                      name="descResponsavelResp"
                      label="Responsável"
                      reactHookForm={hookForm}
                      required={hookForm.watch("tipoRespEquipamento") === 1}
                    />
                  ) : (
                    <DropDownFuncionario
                      name="idFuncionarioResp"
                      required={hookForm.watch("tipoRespEquipamento") == 0}
                      readOnly={saving}
                      reactHookForm={hookForm}
                    />
                  )}
                  <FormControlLabel
                    name="tipoRespEquipamento"
                    control={
                      <Checkbox
                        checked={hookForm.watch("tipoRespEquipamento") === 1}
                        onChange={(e) => hookForm.setValue("tipoRespEquipamento", e.target.checked ? 1 : 0)}
                      />
                    }
                    label="Preencher o responsável"
                    disabled={saving}
                  />
                </Box>


                <Box display="flex" flexDirection="column" gap={2}>
                  <FormLabel>Calibrado/Verificado Por</FormLabel>
                  {hookForm.watch("tipoCaliVeriiPor") === 3 ? (
                    <DefaultFormInput
                      name="descResponsavelMonit"
                      label="Responsável"
                      reactHookForm={hookForm}
                      required={hookForm.watch("tipoCaliVeriiPor") === 3}
                    />
                  ) : (
                    <>
                      <FormControl size="small">
                        <RadioGroup
                          row
                          value={hookForm.watch("tipoCaliVeriiPor") ?? 1}
                          onChange={(e) => hookForm.setValue("tipoCaliVeriiPor", Number(e.target.value))}
                        >
                          <FormControlLabel value={1} control={<Radio />} label="Funcionários" />
                          <FormControlLabel value={0} control={<Radio />} label="Fornecedores" />
                        </RadioGroup>
                      </FormControl>
                      <Box>
                        {hookForm.watch("tipoCaliVeriiPor") === 1 ? (
                          <DropDownFuncionario
                            name="idFuncionarioMonit"
                            label="Funcionário "
                            required={hookForm.watch("tipoCaliVeriiPor") == 1}
                            readOnly={saving}
                            reactHookForm={hookForm}
                          />
                        ) : (
                          <DropDownFornecedor
                            name="idFornecedorMonit"
                            label="Fornecedores "
                            required={hookForm.watch("tipoCaliVeriiPor") == 0}
                            readOnly={saving}
                            reactHookForm={hookForm}
                          />
                        )}
                      </Box>
                    </>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hookForm.watch("tipoCaliVeriiPor") === 3}
                        onChange={(e) => hookForm.setValue("tipoCaliVeriiPor", e.target.checked ? 3 : 1)}
                      />
                    }
                    label="Preencher o responsável"
                    disabled={saving}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                  <DropDownCentroCusto
                    name="idCentroCusto"
                    label="Centro Custo"
                    required={true}
                    readOnly={saving}
                    reactHookForm={hookForm}
                  />
                </Box>
              </Box>

              <Box flex={1} display="flex" flexDirection="column" gap={2}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <FormLabel>Tipo Monitoramento</FormLabel>
                  <FormControl size="small">
                    <RadioGroup
                      row
                      value={hookForm.watch("tipoMonitoramento") ?? "C"}
                      onChange={(e) => hookForm.setValue("tipoMonitoramento", String(e.target.value))}
                    >
                      <FormControlLabel value={"C"} control={<Radio />} label="Calibrado" />
                      <FormControlLabel value={"V"} control={<Radio />} label="Verificado" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControl size="small">
                    <RadioGroup
                      row
                      value={hookForm.watch("tipoFrequenciaData") ?? 0}
                      onChange={(e) => hookForm.setValue("tipoFrequenciaData", Number(e.target.value))}
                    >
                      <FormControlLabel value={1} control={<Radio />} label="Frequência" />
                      <FormControlLabel value={0} control={<Radio />} label="Data Fixa" />
                    </RadioGroup>
                  </FormControl>
                  {(hookForm.watch("tipoFrequenciaData") ?? 1 === 1) ? (
                    <Box display="flex" flexDirection="column" gap={1}>
                      <FormLabel>Frequência</FormLabel>
                      <FormMaskedInput
                        name="frequenciaVerificao"
                        label="Informe o número de dias"
                        reactHookForm={hookForm}
                        mask={"00000"}
                        maskPlaceholder="_____"
                        required={hookForm.watch("tipoFrequenciaData") == 1}
                      />
                    </Box>
                  ) : (
                    <></>
                  )}
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                  <FormDatePickerInput name="dataVerificacao" label="Data de Verificação" reactHookForm={hookForm} required={true} />
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                  <FormDatePickerInput
                    name="dataProxVerificacao"
                    label="Próxima Calibração/Verificação"
                    reactHookForm={hookForm}
                    required={true}
                    readOnly={hookForm.watch("tipoFrequenciaData") === 1}
                  />
                </Box>

                {
                  (selectedEquipamentoMedicao && selectedEquipamentoMedicao.tipoMonitoramento === "V") &&
                  <Box display="flex" flexDirection="column" gap={1}>
                    <FormLabel>Cor:</FormLabel>
                    {
                      cor !== null
                        ? (
                          <div
                            style={{
                              backgroundColor: cor as CSSProperties['color'] ?? 'transparent',
                            }}
                            className="h-[2.85rem] w-[8.0rem] rounded flex flex-col gap-0 justify-center items-center border">
                            <div className="flex flex-col gap-0 justify-center items-center"></div>
                          </div>
                        )
                        : (
                          <div className="w-full text-black dark:text-white">
                            Nenhuma cor ativa
                          </div>
                        )
                    }
                  </Box>
                }
              </Box>
            </Box>

            <Box mt={1} display="flex" flexDirection="column" gap={1}>
              <FormLabel>Observação</FormLabel>
              <DefaultFormInput name="observacao" label="Observação" multiline rows={4} readOnly={saving} reactHookForm={hookForm} />
            </Box>

            <Box mt={1} gap={1} display={"flex"}>
              <Controller
                name="arquivo"
                control={hookForm.control}
                render={({ field }) => (
                  <div className="">
                    {!nomeArquivo ? (
                      <PrimaryButton component="label" variant="contained" disabled={saving}>
                        Escolher arquivo
                        <VisuallyHiddenInput type="file" onChange={(e) => handleFileChange(e, field)} />
                      </PrimaryButton>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span
                          onClick={() => {
                            if (!editmod) return;
                            if (selectedEquipamentoMedicao === null) return;
                            if (selectedEquipamentoMedicao === undefined) return;
                            if (selectedEquipamentoMedicao.idEquipamentoMedicao === null) return;
                            if (selectedEquipamentoMedicao.idEquipamentoMedicao === undefined) return;
                            onDownloadFile(selectedEquipamentoMedicao.idEquipamentoMedicao, 1, selectedEquipamentoMedicao.nomeArquivo ?? "arquivo");
                          }}
                          className={editmod ? "cursor-pointer" : "cursor-not-allowed"}
                        >
                          {nomeArquivo}
                        </span>
                        <IconButton onClick={() => handleRemoveFile(field)}>
                          <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                )}
              />

              <Controller
                name="arquivo1"
                control={hookForm.control}
                render={({ field }) => (
                  <div className="">
                    {!nomeArquivo1 ? (
                      <PrimaryButton component="label" variant="contained" disabled={saving}>
                        Escolher arquivo
                        <VisuallyHiddenInput type="file" onChange={(e) => handleFileChange1(e, field)} />
                      </PrimaryButton>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span
                          onClick={() => {
                            if (!editmod) return;
                            if (selectedEquipamentoMedicao === null) return;
                            if (selectedEquipamentoMedicao === undefined) return;
                            if (selectedEquipamentoMedicao.idEquipamentoMedicao === null) return;
                            if (selectedEquipamentoMedicao.idEquipamentoMedicao === undefined) return;
                            onDownloadFile(selectedEquipamentoMedicao.idEquipamentoMedicao, 2, selectedEquipamentoMedicao.nomeArquivo1 ?? "arquivo");
                          }}
                          className={editmod ? "cursor-pointer" : "cursor-not-allowed"}
                        >
                          {nomeArquivo1}
                        </span>
                        <IconButton onClick={() => handleRemoveFile1(field)}>
                          <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                )}
              />

              {
                selectedEquipamentoMedicao && selectedEquipamentoMedicao.idEquipamentoMedicao && (
                  <PrimaryButton
                    buttonColor="yellow"
                    onClick={() => onOpenHistory(selectedEquipamentoMedicao.idEquipamentoMedicao!)}>
                    Histórico de calibração e verificação do equipamento
                  </PrimaryButton>
                )
              }

            </Box>

            <DialogActions>
              <Box className="w-full flex justify-end">
                <Box className="flex gap-1">
                  <Box className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                    <h1 className="text-sm text-black dark:text-white" onClick={handleClose}>
                      Cancelar
                    </h1>
                  </Box>
                  <Box width="120px">
                    {saving ? (
                      <PrimaryButton buttonColor="primary-green">{"Salvando..."}</PrimaryButton>
                    ) : (
                      <PrimaryButton type="submit" buttonColor="primary-green">
                        {"Salvar"}
                      </PrimaryButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </DialogActions>
          </ModalContainer>
        </ModalLoading>
      </DialogContent>
    </Dialog>
  );
};
